import { DatePicker, INPUT_SIZES } from 'cdk-radial';
import moment, { Moment } from 'moment';
import { useMemo, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { REDUX_SLICE_KEYS } from 'common/constants';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { errors } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { formattedDate, getDateFormatByLocale } from 'utils/dateUtils';
import {
  setPaymentDate,
  getPaymentDateByPrintChecks,
  getPaymentDateByAuthorizePayment
} from './slice';
import { getAccountingDateRange } from './utils';
import type { PaymentDatePickerProps } from './types';
import type { InputSizes } from 'components/StoreSelect/types';

export const PaymentDatePicker = ({
  glCalendarData,
  size,
  setError
}: PaymentDatePickerProps): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const printChecksPaymentDate = useAppSelector(getPaymentDateByPrintChecks);
  const authorizePaymentDate = useAppSelector(getPaymentDateByAuthorizePayment);
  const selectedTab = useAppSelector(getSelectedTab);
  const getPaymentDate = () => {
    if (selectedTab === REDUX_SLICE_KEYS.printChecks) {
      return printChecksPaymentDate;
    }

    return authorizePaymentDate;
  };
  const paymentDate = getPaymentDate();
  const [hasError, setHasError] = useState(false);
  const intl = useIntl();
  const displayDate = getDateFormatByLocale(intl.locale);
  const minDate = new Date(1900, 1, 1);
  const maxDate = new Date(2100, 12, 31);
  const [startDate, endDate] = getAccountingDateRange(glCalendarData);

  const blockedDateRanges = [
    {
      endDate: moment(startDate),
      startDate: moment(minDate)
    },
    {
      endDate: moment(maxDate),
      startDate: moment(endDate)
    }
  ];

  const momentPaymentDate = useMemo(
    () => (paymentDate ? moment(paymentDate) : null),
    [paymentDate]
  );

  useEffect(() => {
    const isValid = moment(paymentDate).isBetween(startDate, endDate);
    dispatch(
      setPaymentDate({
        paymentDate,
        selectedTab,
        isPaymentDateValid: isValid
      })
    );
  }, [paymentDate, startDate, endDate, dispatch, selectedTab]);

  const dateInputProps = {
    size: size || (INPUT_SIZES as InputSizes).SMALL,
    isRequired: true,
    hasError,
    errorMessage: intl.formatMessage(errors.invalidPaymentDate),
    onError: (errorMessages: string[]) => {
      const hasMessages = errorMessages.length > 0;
      setHasError(hasMessages);
      if (setError) {
        setError(hasMessages);
      }
    }
  };

  const handlePaymentDateChange = (date: Moment | null) => {
    dispatch(
      setPaymentDate({
        paymentDate: formattedDate(date?.toISOString() as string),
        selectedTab
      })
    );
  };

  return (
    <DatePicker
      allowDaysInPast
      blockedDateRanges={blockedDateRanges}
      date={momentPaymentDate}
      dateFormat={displayDate}
      dateInputProps={dateInputProps}
      dataTestId={`payment-date-datepicker-${selectedTab}`}
      id={`payment-date-datepicker-${selectedTab}`}
      isRequired
      label={
        <FormattedMessage
          description="Label for payment date date picker"
          defaultMessage="Payment Date"
        />
      }
      name="payment-date-picker"
      onDateChange={handlePaymentDateChange}
    />
  );
};
